import { createContext, useReducer, useRef, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
// firestore
import { doc, query, deleteDoc, collection, onSnapshot } from "firebase/firestore";
import { db } from "../services/firebase";
// products
import actionTypes from "../common/actionTypes";
import { bisNotificationsReducer } from "../reducers";
// context
import useAuthContext from "../hooks/useAuthContext";
import useActivityLogsContext from "../hooks/useActivityLogsContext";
// constants
import { APP_ROUTES, DATABASE_STATUTES } from "../common/constants";
import { LOG_TYPES } from "./ActivityLogsContext";
//
import axios from "../utils/axios";

// BIS constants
const SHOPS = "Shops";
const BIS = "BackInStockNotifications";
const EXPORT_BIS_CSV_URL = "shop/bis/export";

const initialState = {
  data: [],
  status: DATABASE_STATUTES.NOT_LOADED,
  error: null,
};

const BISNotificationsContext = createContext({
  ...initialState,
  deleteBISNotification: () => Promise.resolve(),
  exportBISNotificationCSV: () => Promise.resolve(),
});

const BISNotificationsProvider = ({ children }) => {
  const [state, dispatch] = useReducer(bisNotificationsReducer, initialState);

  const bisCollectionRef = useRef(null);

  const { user } = useAuthContext();
  const { writeLog } = useActivityLogsContext();

  const { pathname } = useLocation();
  const fetchingIsActive = pathname.startsWith(APP_ROUTES.BACKINSTOCK) || pathname.startsWith(APP_ROUTES.ORDERS.ROOT);

  useEffect(() => {
    if (user?.domain && fetchingIsActive) {
      dispatch({
        type: actionTypes.FETCH_BIS_NOTIFICATIONS,
      });

      bisCollectionRef.current = collection(db, SHOPS, user.domain, BIS);

      try {
        return onSnapshot(query(bisCollectionRef.current), (snapshot) => {
          dispatch({
            type: actionTypes.FETCH_BIS_NOTIFICATIONS_SUCCESS,
            payload: snapshot.docs.map((doc) => ({
              ...doc.data(),
              id: doc.id,
            })),
          });
        });
      } catch (error) {
        dispatch({
          type: actionTypes.FETCH_BIS_NOTIFICATIONS_FAILURE,
          payload: error,
        });
      }
    }
  }, [user?.domain, fetchingIsActive]);

  const deleteBISNotification = async ({ id }) => {
    await deleteDoc(doc(bisCollectionRef.current, id));

    await writeLog(LOG_TYPES.DELETE_BIS_NOTIFICATION);
  };

  const exportBISNotificationCSV = async (interval) => {
    try {
      dispatch({ type: actionTypes.EXPORT_BIS_CSV });

      const {
        data: { size },
      } = await axios.get(EXPORT_BIS_CSV_URL, {
        params: {
          interval,
        },
      });

      dispatch({ type: actionTypes.EXPORT_BIS_CSV_SUCCESS });

      return Promise.resolve(size);
    } catch (error) {
      dispatch({
        type: actionTypes.EXPORT_BIS_CSV_FAILURE,
        payload: error,
      });

      return Promise.reject(error);
    }
  };

  const memoizedState = useMemo(
    () => ({
      ...state,
      deleteBISNotification,
      exportBISNotificationCSV,
    }),
    [state]
  );

  return <BISNotificationsContext.Provider value={memoizedState}>{children}</BISNotificationsContext.Provider>;
};

export { BISNotificationsContext, BISNotificationsProvider };
